import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getSaasApiBaseUrl } from '../../../utils/saasApiBaseUrl';
import {
  resolvePasswordResetRouting,
  shouldRedirectToUrl,
} from '@saas-foundation/shared-frontend/utils/passwordResetContext';
import PasswordInput from '@saas-foundation/shared-frontend/auth/PasswordInput';

const getApiBaseUrl = getSaasApiBaseUrl;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [loginUrl, setLoginUrl] = useState('/login');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) setToken(tokenFromUrl);
  }, [searchParams]);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const routing = await resolvePasswordResetRouting(
        getApiBaseUrl(),
        tokenFromUrl,
        searchParams.get('app'),
      );
      if (cancelled) {
        return;
      }
      if (routing.loginUrl) {
        setLoginUrl(routing.loginUrl);
      }
      if (routing.resetUrl && shouldRedirectToUrl(routing.resetUrl)) {
        window.location.replace(routing.resetUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const requirements = useMemo(() => {
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 8;
    return {
      allMet: hasLowercase && hasUppercase && hasNumber && hasMinLength,
      items: [
        { text: '1 lowercase letter', met: hasLowercase },
        { text: '1 uppercase letter', met: hasUppercase },
        { text: '1 number', met: hasNumber },
        { text: '8 characters minimum', met: hasMinLength },
      ],
    };
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Reset link is invalid or missing.');
      return;
    }
    if (!requirements.allMet) {
      setError('Please meet all password requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/user/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || data.message || 'Password reset failed.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToSignIn = () => {
    window.location.href = loginUrl;
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
        <div className="max-w-md w-full mx-auto bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Password updated</h2>
          <p className="text-sm text-gray-600 mb-4">You can now sign in with your new password.</p>
          <button
            type="button"
            onClick={goToSignIn}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
      <div className="max-w-md w-full mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">AgenticPractice</h1>
        <h2 className="text-lg font-semibold text-gray-800 text-center mb-6">Create a new password</h2>
        <div className="bg-white rounded-lg shadow p-6">
          {!token && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              This link is invalid or expired.{' '}
              <Link to="/forgot-password" className="underline font-medium">Request a new one</Link>.
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={!token || loading}
              />
              <ul className="mt-2 grid grid-cols-2 gap-1">
                {requirements.items.map((req) => (
                  <li key={req.text} className="text-xs flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${req.met ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className={req.met ? 'text-gray-900' : 'text-gray-500'}>{req.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <PasswordInput
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={!token || loading}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={!token || loading || !requirements.allMet}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
          <p className="mt-4 text-center">
            <button type="button" onClick={goToSignIn} className="text-sm text-blue-600 hover:text-blue-700">
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
